/**
 * What the guide looks like: a dim sheet with a hole cut in it, and a bubble.
 *
 * The dim is an SVG mask rather than four positioned divs. A mask gives one
 * element to fade, a rounded cut-out for free, and no seams between panels when
 * the anchor sits near an edge.
 *
 * Styled inline throughout. src/index.css is committed pre-compiled Tailwind, so
 * any utility class not already in that bundle renders nothing at all.
 *
 * The sheet swallows every pointer event, including over the cut-out: the
 * spotlight shows what to look at, it is not an invitation to use it mid-tour.
 * Letting the underlying control fire would navigate out from under the overlay.
 */

import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { GUIDE_STEPS } from './guideSteps';
import { useGuide } from './useGuide';

/** Above every app surface. Below the image lightbox, which owns max int. */
const GUIDE_Z = 99000000;
/** Breathing room around the spotlit element. */
const PAD = 8;
/** Bubble geometry. */
const BUBBLE_W = 300;
/** Smallest gap the bubble keeps from any viewport edge. */
const BUBBLE_MARGIN = 14;
const BUBBLE_GAP = 14;

export function GuideOverlay() {
  const { visible, transitioning, stepIndex, anchorRect, next, back, skip } = useGuide();
  // Measured, not estimated. Placement needs to know how tall the bubble is to
  // decide above-or-below and to clamp it, and the height changes with the
  // wording of each step — a fixed guess is wrong for every step but one.
  // Before the first measurement this is a reasonable starting value; the ref
  // callback corrects it on the same commit.
  const [bubbleH, setBubbleH] = useState(190);
  const measureBubble = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const h = el.offsetHeight;
    // Guarded so a stable height cannot cause a render loop.
    setBubbleH(prev => (Math.abs(prev - h) > 1 ? h : prev));
  }, []);

  // `visible`, not `active`: while a higher-priority popup holds the queue slot
  // the tour keeps its place and its step but must not be drawn, or it renders
  // underneath that popup and both are on screen at once.
  if (!visible || stepIndex < 0) return null;
  const step = GUIDE_STEPS[stepIndex];
  if (!step) return null;

  // The bubble is ALWAYS shown.
  //
  // It used to hide whenever there was no rect yet, which is fine between two
  // steps on one screen but wrong across a navigation: the spotlight is dropped
  // on a screen change, so hiding the bubble as well left nothing on screen but
  // a dim sheet while the next screen loaded. With no rect it simply centres,
  // then travels to the spotlight when the anchor turns up. `resolving` is left
  // in the signature because the overlay still reads it for nothing else, and
  // removing the prop would be a wider change than this needs.

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const hole = anchorRect
    ? {
        x: Math.max(0, anchorRect.left - PAD),
        y: Math.max(0, anchorRect.top - PAD),
        w: Math.min(vw, anchorRect.width + PAD * 2),
        h: Math.min(vh, anchorRect.height + PAD * 2),
      }
    : null;

  // Below the spotlight when there is room, above it otherwise, and centred when
  // there is no spotlight at all (the closing step).
  const placeBelow = hole
    ? (step.placement === 'below'
        || (step.placement !== 'above' && hole.y + hole.h + BUBBLE_GAP + bubbleH < vh))
    : true;

  // Positioned from the TOP and then clamped into the viewport.
  //
  // It used to anchor the above-placement from the bottom edge instead
  // (`bottom: vh - hole.y + GAP`), which quietly breaks for a tall anchor near
  // the top of the screen: the share-profile card starts high and runs most of
  // the way down, so that expression put the bubble's bottom edge a few pixels
  // from the top of the viewport and the whole thing sat off-screen above it.
  // A single clamped `top` cannot produce that, whatever shape the anchor is.
  const rawTop = hole
    ? (placeBelow ? hole.y + hole.h + BUBBLE_GAP : hole.y - BUBBLE_GAP - bubbleH)
    : vh / 2 - bubbleH / 2;
  const bubbleTop = Math.min(
    Math.max(BUBBLE_MARGIN, rawTop),
    Math.max(BUBBLE_MARGIN, vh - bubbleH - BUBBLE_MARGIN),
  );

  // Clamped so a spotlight near either edge does not push the bubble off screen.
  const bubbleLeft = hole
    ? Math.min(Math.max(12, hole.x + hole.w / 2 - BUBBLE_W / 2), Math.max(12, vw - BUBBLE_W - 12))
    : Math.max(12, vw / 2 - BUBBLE_W / 2);

  const isLast = stepIndex === GUIDE_STEPS.length - 1;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`App guide, step ${stepIndex + 1} of ${GUIDE_STEPS.length}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: GUIDE_Z,
        // Every tap belongs to the guide while it is up.
        pointerEvents: 'auto',
      }}
    >
      {/* Dim + cut-out */}
      <svg
        width={vw}
        height={vh}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        aria-hidden
      >
        <defs>
          <mask id="guide-spotlight-mask">
            {/* White shows the dim, black punches the hole. */}
            <rect x={0} y={0} width={vw} height={vh} fill="white" />
            {hole && (
              <rect
                x={hole.x} y={hole.y} width={hole.w} height={hole.h}
                rx={16} ry={16} fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x={0} y={0} width={vw} height={vh}
          fill="rgba(0,0,0,0.72)"
          mask="url(#guide-spotlight-mask)"
        />
      </svg>

      {/* Ring around the spotlight, so the edge of the hole reads as deliberate. */}
      {hole && (
        <div
          style={{
            position: 'absolute',
            left: hole.x, top: hole.y, width: hole.w, height: hole.h,
            borderRadius: 16,
            border: '2px solid rgba(68,120,234,0.95)',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.18), 0 0 18px 2px rgba(68,120,234,0.45)',
            pointerEvents: 'none',
            // Travels to the next anchor instead of teleporting.
            transition: 'left 0.25s ease, top 0.25s ease, width 0.25s ease, height 0.25s ease',
          }}
        />
      )}

      {!transitioning && (
        <div
          ref={measureBubble}
          style={{
            position: 'absolute',
            left: bubbleLeft,
            top: bubbleTop,
            width: BUBBLE_W,
            maxWidth: 'calc(100vw - 24px)',
            backgroundColor: '#161616',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 20,
            padding: 18,
            boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
            transition: 'left 0.25s ease, top 0.25s ease',
          }}
        >
          <p style={{
            margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.40)',
          }}>
            {stepIndex + 1} of {GUIDE_STEPS.length}
          </p>

          <h2 style={{
            margin: '8px 0 0', fontSize: 18, fontWeight: 700, color: '#ffffff',
            lineHeight: 1.25,
          }}>
            {step.title}
          </h2>

          <p style={{
            margin: '8px 0 0', fontSize: 14, lineHeight: 1.5,
            color: 'rgba(255,255,255,0.72)',
          }}>
            {step.body}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 }}>
            {/* Skip stays reachable on every step, including the last, so there
                is always one tap out of the overlay. */}
            <button
              type="button"
              onClick={skip}
              style={{
                background: 'none', border: 'none', padding: '10px 4px',
                fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.45)',
                cursor: 'pointer',
              }}
            >
              Skip
            </button>

            <div style={{ flex: 1 }} />

            {stepIndex > 0 && (
              <button
                type="button"
                onClick={back}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: 12, padding: '10px 16px',
                  fontSize: 14, fontWeight: 700, color: '#ffffff', cursor: 'pointer',
                }}
              >
                Back
              </button>
            )}

            <button
              type="button"
              onClick={next}
              style={{
                background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)',
                border: 'none', borderRadius: 12, padding: '10px 18px',
                fontSize: 14, fontWeight: 700, color: '#ffffff', cursor: 'pointer',
              }}
            >
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
